import Axios from 'axios';
import { setupCache } from 'axios-cache-interceptor';
import FileSaver from 'file-saver';

const api = setupCache(Axios);

function createCookie(name, value, expire = 30) {
  const date = new Date();
  date.setTime(date.getTime() + (expire * 60 * 1000));
  document.cookie = `${name}=${value}; expires=${date.toGMTString()}; path=/; SameSite=strict`;
}

function readCookie(name) {
  const nameEQ = `${name}=`;
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i += 1) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
}

function eraseCookie(name) {
  createCookie(name, '', -1);
}

function VMVB() {
  this.login = (username, password, callback, twoFactor = null) => {
    this.doCall('login', 'POST', callback, {
      username,
      password,
      two_factor: twoFactor,
    });
  };

  this.loginHash = (email, hash, callback) => {
    this.doCall('login/hash', 'POST', callback, {
      email,
      hash,
    });
  };

  this.register = (
    username,
    email,
    callback,
    goal = null,
    advisor = null,
    monthly = null,
    years = null,
    target = null,
    start = null,
  ) => {
    this.doCall('register', 'POST', callback, {
      username,
      email,
      goal,
      advisor,
      monthly,
      years,
      target,
      start,
      utm: readCookie('utmcookie'),
    });
  };

  this.become = (user, callback) => {
    this.doCall(`become/${user}`, 'POST', callback, null, true);
  };

  this.get2fa = (callback) => {
    this.doCall('2fa', 'GET', callback, null, true);
  };

  this.remove2fa = (callback) => {
    this.doCall('2fa', 'DELETE', callback, null, true);
  };

  this.set2fa = (formattedKey, formattedToken, type, callback) => {
    this.doCall('2fa', 'POST', callback, {
      formattedKey,
      formattedToken,
      type,
    }, true);
  };

  this.email2fa = (formattedKey, callback) => {
    this.doCall('2fa/email', 'POST', callback, {
      formattedKey,
    }, true);
  };

  this.adminRemove2fa = (user, callback) => {
    this.doCall(`user/${user}/2fa`, 'DELETE', callback, null, true);
  };

  this.unbecome = (callback) => {
    this.doCall('unbecome', 'POST', callback, null, true);
  };

  this.refresh = () => {
    this.doCall('refresh', 'POST', () => { this.refreshing = false; }, null, true);
  };

  this.logout = () => {
    eraseCookie('vmvb_id');
    eraseCookie('vmvb_token');
    eraseCookie('vmvb_name');
    eraseCookie('vmvb_role');
    eraseCookie('vmvb_expire');
    eraseCookie('vmvb_nps');
    eraseCookie('vmvb_2fa');
  };

  this.addNps = (score, callback) => {
    eraseCookie('vmvb_nps');
    this.doCall('nps', 'POST', callback, {
      score,
    }, true);
  };

  this.getAdminNps = (callback) => {
    this.doCall('admin/nps', 'GET', callback, null, true);
  };

  this.resetPassword = (email, callback) => {
    this.doCall('reset', 'POST', callback, {
      email,
    });
  };

  this.forgotUsername = (email, callback) => {
    this.doCall('forgot', 'POST', callback, {
      email,
    });
  };

  this.checkReset = (email, hash, callback) => {
    this.doCall('reset', 'GET', callback, {
      email,
      hash,
    });
  };

  this.doReset = (email, hash, password, callback) => {
    this.doCall('reset', 'PUT', callback, {
      email,
      hash,
      password,
    });
  };

  this.changePassword = (password, newpassword, callback) => {
    this.doCall('password', 'PUT', callback, {
      password,
      newpassword,
    }, true);
  };

  this.getPositions = (port, query, callback) => {
    if (!query) {
      query = {};
    }
    query.cache = 15;
    this.doCall(`port/${port}/positions`, 'GET', callback, query, true);
  };

  this.getPerformanceNear = (port, query, callback) => {
    this.doCall(`port/${port}/performanceNear`, 'GET', callback, query, true);
  };

  this.getPerformanceOn = (port, query, callback) => {
    this.doCall(`port/${port}/performanceOn`, 'GET', callback, query, true);
  };

  this.getPort = (port, callback) => {
    this.doCall(`port/${port}`, 'GET', callback, null, true);
  };

  this.getGoal = (goal, callback) => {
    this.doCall('goal', 'POST', callback, goal);
  };

  this.getProfit = (profit, callback) => {
    this.doCall('profit', 'POST', callback, profit);
  };

  this.getSat = (port, query, callback) => {
    this.doCall(`port/${port}/sat`, 'GET', callback, query, true);
  };

  this.newSat = (
    startValue,
    goalValue,
    years,
    targetDate,
    depositValue,
    depositInterval,
    depositStart,
    withdrawValue,
    withdrawInterval,
    withdrawStart,
    profile,
    maxProfile,
    callback,
    lifecycle = true,
  ) => {
    this.doCall('sat', 'POST', callback, {
      start: startValue,
      goal: goalValue,
      years,
      target_date: targetDate,
      deposit: depositValue,
      deposit_interval: depositInterval,
      deposit_date: depositStart,
      withdraw: withdrawValue,
      withdraw_interval: withdrawInterval,
      withdraw_date: withdrawStart,
      profile,
      max_profile: maxProfile,
      lifecycle,
    }, true);
  };

  this.freeSat = (
    goalValue,
    years,
    start,
    depositValue,
    depositInterval,
    profile,
    callback,
    lifecycle = true,
  ) => {
    this.doCall('freesat', 'POST', callback, {
      goal: goalValue,
      years,
      start,
      deposit: depositValue,
      deposit_interval: depositInterval,
      profile,
      lifecycle,
    });
  };

  this.getCashflow = (port, callback) => {
    this.doCall(`port/${port}/cashflow`, 'GET', callback, null, true);
  };

  this.createCashflow = (port, value, interval, startDate, endDate, inflation, callback) => {
    this.doCall(`port/${port}/cashflow`, 'POST', callback, {
      value,
      interval,
      start_date: startDate,
      end_date: endDate,
      inflation,
    }, true);
  };

  this.modifyCashflow = (
    port,
    cashflow,
    value,
    interval,
    startDate,
    endDate,
    inflation,
    callback,
  ) => {
    this.doCall(`port/${port}/cashflow/${cashflow}`, 'PUT', callback, {
      value,
      interval,
      start_date: startDate,
      end_date: endDate,
      inflation,
    }, true);
  };

  this.removeCashflow = (port, cashflow, callback) => {
    this.doCall(`port/${port}/cashflow/${cashflow}`, 'DELETE', callback, null, true);
  };

  this.getTransactions = (port, callback, filter, page, maxPerPage, order = 'date desc') => {
    this.doCall(`port/${port}/transactions`, 'GET', callback, {
      filter,
      page,
      maxPerPage,
      order,
      cache: 15,
    }, true);
  };

  this.getTransactionsAsset = (port, asset, callback) => {
    this.doCall(`port/${port}/transactions/${asset}`, 'GET', callback, { cache: 15 }, true);
  };

  this.getPortAsset = (port, asset, callback) => {
    this.doCall(`port/${port}/asset/${asset}`, 'GET', callback, { cache: 15 }, true);
  };

  this.getFiles = (port, callback) => {
    this.doCall(`port/${port}/files`, 'GET', callback, {
      cache: 15,
    }, true);
  };

  this.getFile = (port, file) => {
    this.doCall(`port/${port}/download`, 'GET', (blob) => {
      if (blob.status === 200) {
        if (typeof cordova !== 'undefined') {
          this.cordovaFileSaver(
            file.replace('files-', 'overzicht-').replace('-per-', '-').replace('-mon-', '-'),
            blob.blob,
            blob.blob.type,
          );
        } else {
          FileSaver.saveAs(
            blob.blob,
            file.replace('files-', 'overzicht-').replace('-per-', '-').replace('-mon-', '-'),
            { type: 'application/pdf' },
          );
        }
      } else if (blob.status === 404) {
        // eslint-disable-next-line
        alert('Het bestand is niet (meer) beschikbaar');
      }
    }, { file, cache: 15 }, true, true, false, true);
  };

  /* eslint-disable */
  this.cordovaFileSaver = function (fileName, fileData, mimeType) {
    // Get access to the file system
    window.resolveLocalFileSystemURL(cordova.file.dataDirectory, function (fileSystem) {
      // Create the file.
      fileSystem.getFile(fileName, { create: true, exclusive: false }, function (entry) {
        // After you save the file, you can access it with this URL
        let myFileUrl = entry.toURL();
        entry.createWriter(function (writer) {
          writer.onwriteend = function (e) {
            console.log('Successfully saved file to ' + myFileUrl);
            // Open file
            cordova.plugins.fileOpener2.open(cordova.file.dataDirectory + fileName, mimeType, {
              error: function (e) {
                console.log('Error status: ' + e.status + ' - Error message: ' + e.message);
                console.log('Path' + filePath, mimeType);
              },
              success: function () {
                console.log('File opened successfully');
              }
            });
          };
          // Write to the file
          writer.write(fileData);
        }, function (error) {
          console.log("Error: Could not create file writer, " + error.code);
        });
      }, function (error) {
        console.log("Error: Could not create file, " + error.code);
      });
    }, function (evt) {
      console.log("Error: Could not access file system, " + evt.target.error.code);
    });
  }
  /* eslint-enable */

  this.getPerformance = (port, callback) => {
    this.doCall(`port/${port}/performance`, 'GET', callback, { cache: 15 }, true);
  };

  this.getPorts = (callback) => {
    this.doCall('port', 'GET', callback, { cache: 15 }, true);
  };

  this.getAdminPorts = (callback) => {
    this.doCall('admin/port', 'GET', callback, null, true);
  };

  this.getDailyDelta = (port, callback) => {
    this.doCall(`port/${port}/dailyDelta`, 'GET', callback, { cache: 15 }, true);
  };

  this.portActionRequired = (port, callback) => {
    this.doCall(`port/${port}/actionRequired`, 'POST', callback, null, true);
  };

  this.stopActionRequired = (port, callback) => {
    this.doCall(`port/${port}/actionRequired`, 'DELETE', callback, null, true);
  };

  this.getAdminRisks = (callback) => {
    this.doCall('admin/risk', 'GET', callback, null, true);
  };

  this.getBandwidthRisks = (
    callback,
    filter,
    page,
    maxPerPage,
    sort = null,
    sortDirection = null,
  ) => {
    this.doCall('admin/bandwidthrisks', 'GET', callback, {
      filter,
      page,
      maxPerPage,
      sort,
      sortDirection,
    }, true);
  };

  this.getAdminPortloss = (callback) => {
    this.doCall('admin/portloss', 'GET', callback, null, true);
  };

  this.getAdminScoreMessages = (callback) => {
    this.doCall('admin/message/scores', 'GET', callback, null, true);
  };

  this.getAdminProfileCalcs = (callback) => {
    this.doCall('admin/profile', 'GET', callback, null, true);
  };

  this.getUsers = (callback, filter, page, maxPerPage = 20) => {
    this.doCall('user', 'GET', callback, { filter, page, maxPerPage }, true);
  };

  this.getUsersQuick = (callback) => {
    this.doCall('admin/user', 'GET', callback, null, true);
  };

  this.getUserDetails = (callback) => {
    this.doCall('details', 'GET', callback, null, true);
  };

  this.getPortUsers = (port, callback) => {
    this.doCall(`port/${port}/users`, 'GET', callback, null, true);
  };

  this.addUser = (name, username, email, role, password, salt, callback) => {
    this.doCall('user', 'POST', callback, {
      name,
      username,
      email,
      role,
      password,
      salt,
    }, true);
  };

  this.addUserPort = (user, port, callback) => {
    this.doCall(`user/${user}/port`, 'POST', callback, {
      port,
    }, true);
  };

  this.modifyPortAdmin = (
    port,
    manager,
    profile,
    iban,
    target,
    targetDate,
    inflation,
    name,
    alert,
    intakeDate,
    callback,
  ) => {
    this.doCall(`port/${port}`, 'PUT', callback, {
      manager,
      profile,
      iban,
      target,
      targetdate: targetDate,
      inflation,
      name,
      alert,
      intakedate: intakeDate,
    }, true);
  };

  this.modifyPort = (port, name, alert, intakeDate, callback) => {
    this.doCall(`port/${port}`, 'PUT', callback, {
      name,
      alert,
      intakedate: intakeDate,
    }, true);
  };

  this.getScores = (port, callback) => {
    this.doCall(`port/${port}/scores`, 'GET', callback, { cache: 15 }, true);
  };

  this.sendUserPassword = (user, callback) => {
    this.doCall(`user/${user}/reset`, 'POST', callback, null, true);
  };

  this.getUserLogs = (user, callback) => {
    this.doCall(`user/${user}/logs`, 'GET', callback, null, true);
  };

  this.getUserIds = (callback, filter, page, maxPerPage) => {
    this.doCall('user/search/userIds', 'GET', callback, { filter, page, maxPerPage }, true);
  };

  this.getUserCRM = (user, callback) => {
    this.doCall(`user/${user}/crm`, 'GET', callback, null, true);
  };

  this.pushIntakeCRM = (intake, callback) => {
    this.doCall(`intake/${intake}/crm`, 'POST', callback, null, true);
  };

  this.getIntakeLogs = (intake, callback) => {
    this.doCall(`intake/${intake}/logs`, 'GET', callback, null, true);
  };

  this.removeUserPort = (user, port, callback) => {
    this.doCall(`user/${user}/port/${port}`, 'DELETE', callback, null, true);
  };

  this.modifyUser = (user, name, username, email, role, callback) => {
    this.doCall(`user/${user}`, 'PUT', callback, {
      name,
      username,
      email,
      role,
    }, true);
  };

  this.deleteUser = (user, callback) => {
    this.doCall(`user/${user}`, 'DELETE', callback, null, true);
  };

  this.getMessages = (callback, filter, page, maxPerPage) => {
    this.doCall('message', 'GET', callback, { filter, page, maxPerPage }, true);
  };

  this.getIntakeGroups = (callback, redo, filter) => {
    this.doCall('admin/intakegroups', 'GET', callback, {
      redo,
      filter,
    }, true);
  };

  this.getAdminIntakes = (callback, filter, order, page = 0, maxPerPage = 20) => {
    this.doCall('admin/intake', 'GET', callback, {
      filter,
      order,
      page,
      maxPerPage,
    }, true);
  };

  this.getAdminIntakeRedos = (callback, filter, order, page = 0, maxPerPage = 20) => {
    this.doCall('admin/intake/redos', 'GET', callback, {
      filter,
      order,
      page,
      maxPerPage,
    }, true);
  };

  this.getAdminIntakeOld = (callback) => {
    this.doCall('admin/intake/old', 'GET', callback, null, true);
  };

  this.getAdminIntakeCompleted = (callback, filter, page, maxPerPage) => {
    this.doCall('admin/intake/completed', 'GET', callback, { filter, page, maxPerPage }, true);
  };
  this.getAdminIntakeDeleted = (callback, filter, page, maxPerPage) => {
    this.doCall('admin/intake/deleted', 'GET', callback, { filter, page, maxPerPage }, true);
  };

  this.getIntakes = (callback) => {
    this.doCall('intake', 'GET', callback, null, true);
  };

  this.createIntake = (callback, goal = null) => {
    this.doCall('intake', 'POST', callback, { goal }, true);
  };

  this.redoIntake = (port, callback) => {
    this.doCall(`port/${port}/intake`, 'POST', callback, null, true);
  };

  this.getPortIntake = (port, callback) => {
    this.doCall(`port/${port}/intake`, 'GET', callback, null, true);
  };

  this.updateIntake = (id, intake, callback) => {
    this.doCall(`intake/${id}`, 'PUT', callback, { intake }, true);
  };

  this.addFileIntake = (prefix, intake, files, callback) => {
    const formData = new FormData();
    for (let i = 0; i < files.length; i += 1) {
      formData.append('photos', files[i], prefix + files[i].name);
    }
    this.doCall(`intake/${intake}/upload`, 'POST', callback, formData, true, false, true);
  };

  this.addFile = (files, callback, port = null) => {
    const formData = new FormData();
    for (let i = 0; i < files.length; i += 1) {
      formData.append('photos', files[i]);
    }
    this.doCall(`upload?port=${port}`, 'POST', callback, formData, true, false, true);
  };

  this.getPublicFile = (file) => {
    this.doCall(`download/${file}`, 'GET', (blob) => {
      if (blob.status === 200) {
        FileSaver.saveAs(blob.blob, file);
      } else if (blob.status === 404) {
        // eslint-disable-next-line
        alert('Het bestand is niet (meer) beschikbaar');
      }
    }, null, true, true);
  };

  this.addUploadPublicFile = (user, files, callback) => {
    const formData = new FormData();
    for (let i = 0; i < files.length; i += 1) {
      formData.append('photos', files[i]);
    }
    this.doCall(`user/${user}/upload`, 'POST', callback, formData, true, false, true);
  };

  this.deleteUserPublicFile = (user, file, callback) => {
    this.doCall(`user/${user}/publicFiles/${file}`, 'DELETE', callback, null, true);
  };

  this.getUserPublicFile = (user, file) => {
    this.doCall(`user/${user}/publicFiles/${file}`, 'GET', (blob) => {
      if (blob.status === 200) {
        FileSaver.saveAs(blob.blob, file);
      } else if (blob.status === 404) {
        // eslint-disable-next-line
        alert('Het bestand is niet (meer) beschikbaar');
      }
    }, null, true, true);
  };

  this.getUserPublicFiles = (user, callback) => {
    this.doCall(`user/${user}/publicFiles`, 'GET', callback, null, true);
  };

  this.deleteUserFile = (user, file, callback) => {
    this.doCall(`user/${user}/download/${file}`, 'DELETE', callback, null, true);
  };

  this.getUserFile = (user, file) => {
    this.doCall(`user/${user}/download/${file}`, 'GET', (blob) => {
      if (blob.status === 200) {
        FileSaver.saveAs(blob.blob, file);
      } else if (blob.status === 404) {
        // eslint-disable-next-line
        alert('Het bestand is niet (meer) beschikbaar');
      }
    }, null, true, true);
  };

  this.getUserFiles = (user, callback) => {
    this.doCall(`user/${user}/download`, 'GET', callback, null, true);
  };

  this.getArchiveFiles = (port, callback) => {
    this.doCall(`archive/${port}/files`, 'GET', callback, null, true);
  };

  this.moveArchiveFiles = (port, newport, callback) => {
    this.doCall(`archive/${port}/files`, 'PUT', callback, { newport }, true);
  };

  this.getArchiveFile = (port, file) => {
    this.doCall(`archive/${port}/file/${file}`, 'GET', (blob) => {
      if (blob.status === 200) {
        FileSaver.saveAs(blob.blob, file);
      } else {
        // eslint-disable-next-line
        alert('Het bestand is niet (meer) beschikbaar');
      }
    }, null, true, true);
  };

  this.moveArchiveFile = (port, file, newport, callback) => {
    this.doCall(`archive/${port}/file/${file}`, 'PUT', callback, { newport }, true);
  };

  this.deleteArchiveFile = (port, file, callback) => {
    this.doCall(`archive/${port}/file/${file}`, 'DELETE', callback, null, true);
  };

  this.uploadArchiveFile = (port, files, callback) => {
    const formData = new FormData();
    for (let i = 0; i < files.length; i += 1) {
      formData.append('photos', files[i]);
    }
    this.doCall(`archive/${port}/upload`, 'POST', callback, formData, true, false, true);
  };

  this.getReturns = (callback) => {
    this.doCall('ureturn', 'GET', callback, null, true);
  };

  this.getReturn = (date, callback) => {
    this.doCall(`ureturn/${date}`, 'GET', callback, null, true);
  };

  this.getReturnToday = (callback) => {
    this.doCall('ureturn/today', 'GET', callback, null, true);
  };

  this.addReturn = (date, uReturn, callback) => {
    this.doCall('ureturn', 'POST', callback, {
      date,
      uReturn,
    }, true);
  };

  this.deleteReturn = (date, callback) => {
    this.doCall(`ureturn/${date}`, 'DELETE', callback, null, true);
  };

  this.deleteIntakeFile = (intake, file, callback) => {
    this.doCall(`intake/${intake}/download/${file}`, 'DELETE', callback, null, true);
  };

  this.getIntakeFile = (intake, file) => {
    this.doCall(`intake/${intake}/download/${file}`, 'GET', (blob) => {
      if (blob.status === 200) {
        FileSaver.saveAs(blob.blob, file);
      } else if (blob.status === 404) {
        // eslint-disable-next-line
        alert('Het bestand is niet (meer) beschikbaar');
      }
    }, null, true, true);
  };

  this.getPayouts = (port, callback) => {
    this.doCall(`port/${port}/payouts`, 'GET', callback, null, true);
  };

  this.getAdminPayouts = (callback) => {
    this.doCall('admin/payouts', 'GET', callback, null, true);
  };

  this.getAdminPayoutable = (callback) => {
    this.doCall('admin/payoutable', 'GET', callback, null, true);
  };

  this.modifyPayouts = (
    port,
    years,
    interest,
    initials,
    overtimes,
    payouts,
    forehand,
    startDate,
    lastPayoutDate,
    frequency,
    cashflow,
    calcInterest,
    callback,
  ) => {
    this.doCall(`port/${port}/payouts`, 'PUT', callback, {
      years,
      interest,
      initials,
      overtimes,
      payouts,
      forehand,
      start_date: startDate,
      lastpayout_date: lastPayoutDate,
      frequency,
      cashflow,
      calc_interest: calcInterest,
    }, true);
  };

  this.getPayoutRevision = (port, revision, callback) => {
    this.doCall(`port/${port}/payouts/${revision}`, 'GET', callback, null, true);
  };

  this.getIntake = (intake, callback) => {
    this.doCall(`intake/${intake}`, 'GET', callback, null, true);
  };

  this.searchIntakeCdd = (intake, person, firstName, surname, dateOfBirth, country, callback) => {
    this.doCall(`intake/${intake}/cdd/${person}`, 'GET', callback, {
      firstName,
      surname,
      dateOfBirth,
      country,
    }, true);
  };

  this.downloadIntakeCdd = (intake, person, sessionId, complianceId, callback) => {
    this.doCall(`intake/${intake}/cdd/${person}`, 'POST', callback, {
      sessionId,
      complianceId,
    }, true);
  };

  this.getIntakePrevious = (intake, callback) => {
    this.doCall(`intake/${intake}/previous`, 'GET', callback, null, true);
  };

  this.getIntakeTransactions = (intake, callback) => {
    this.doCall(`intake/${intake}/transaction`, 'GET', callback, null, true);
  };

  this.getIntakeZip = (intake, postcode, number, extra, callback) => {
    this.doCall(`intake/${intake}/zipcode`, 'GET', callback, {
      postcode,
      number,
      extra,
      cache: 15,
    }, true);
  };

  this.sendIntake = (intake, callback) => {
    this.doCall(`intake/${intake}/send`, 'POST', callback, null, true);
  };

  this.adminIntake = (intake, admin, callback) => {
    this.doCall(`intake/${intake}/admin`, 'PUT', callback, { admin }, true);
  };

  this.linkIntakePort = (intake, port, callback) => {
    this.doCall(`intake/${intake}/port`, 'PUT', callback, { port }, true);
  };

  this.modifyIntakeValue = (intake, value, callback) => {
    this.doCall(`intake/${intake}/value`, 'PUT', callback, { value }, true);
  };

  this.linkIntakeUser = (intake, email, name, callback, extra = null, type = null) => {
    this.doCall(`intake/${intake}/user`, 'PUT', callback, {
      email, name, extra, type,
    }, true);
  };

  this.acceptIntake = (intake, callback) => {
    this.doCall(`intake/${intake}/accept`, 'POST', callback, null, true);
  };

  this.intakeIdeal = (intake, issuer, amount, callback) => {
    this.doCall(`intake/${intake}/ideal2`, 'POST', callback, {
      issuer,
      amount,
    }, true);
  };

  this.checkIntakeIdeal = (intake, transaction, callback) => {
    this.doCall(`intake/${intake}/transaction2`, 'POST', callback, { transaction }, true);
  };

  this.signIntake = (intake, transaction, callback) => {
    this.doCall(`intake/${intake}/sign`, 'POST', callback, { transaction }, true);
  };

  this.adminIntakeOffer = (intake, callback) => {
    this.doCall(`intake/${intake}/offer`, 'POST', callback, null, true);
  };

  this.adminIntakeWelcome = (intake, callback) => {
    this.doCall(`intake/${intake}/welcome`, 'POST', callback, null, true);
  };

  this.adminIntakePayouts = (intake, callback) => {
    this.doCall(`intake/${intake}/write`, 'POST', callback, null, true);
  };

  this.completeIntake = (intake, callback, message = true) => {
    this.doCall(`intake/${intake}/complete`, 'POST', callback, { message }, true);
  };

  this.returnIntake = (intake, callback) => {
    this.doCall(`intake/${intake}/return`, 'POST', callback, null, true);
  };

  this.deleteIntake = (intake, callback) => {
    this.doCall(`intake/${intake}`, 'DELETE', callback, null, true);
  };

  this.permanentDeleteIntake = (intake, callback) => {
    this.doCall(`intake/${intake}/permanent`, 'DELETE', callback, null, true);
  };

  this.restoreIntake = (intake, callback) => {
    this.doCall(`intake/${intake}/restore`, 'POST', callback, null, true);
  };

  this.getLogs = (callback, filter, page, maxPerPage) => {
    this.doCall('logs', 'GET', callback, { filter, page, maxPerPage }, true);
  };

  this.getAllLogs = (callback, filter, page, maxPerPage) => {
    this.doCall('logs/all', 'GET', callback, { filter, page, maxPerPage }, true);
  };

  this.getAssets = (callback) => {
    this.doCall('asset', 'GET', callback, null, true);
  };

  this.getStatus = (callback) => {
    this.doCall('status', 'GET', callback, null, true);
  };

  this.getMandates = (callback) => {
    this.doCall('user/mandates', 'GET', callback, null, true);
  };

  this.getOwnerMandates = (user, callback) => {
    this.doCall(`user/${user}/mandates`, 'GET', callback, null, true);
  };

  this.acceptMandate = (user, callback) => {
    this.doCall(`mandate/${user}`, 'PUT', callback, null, true);
  };

  this.removeMandate = (user, callback) => {
    this.doCall(`mandate/${user}`, 'DELETE', callback, null, true);
  };

  this.resendMandate = (user, callback) => {
    this.doCall(`mandate/${user}/resend`, 'PUT', callback, null, true);
  };

  this.getProfiles = (callback) => {
    this.doCall('profile', 'GET', callback, null, true);
  };

  this.getIssuers = (callback) => {
    this.doCall('ideal', 'GET', callback, { cache: 15 }, true);
  };

  this.sendPortLoss = (port, callback) => {
    this.doCall(`port/${port}/loss`, 'POST', callback, null, true);
  };

  this.portNewIdeal = (port, issuer, amount, callback) => {
    this.doCall(`port/${port}/ideal2`, 'POST', callback, {
      issuer,
      amount,
    }, true);
  };

  this.portIdeals = (port, callback) => {
    this.doCall(`port/${port}/ideal`, 'GET', callback, null, true);
  };

  this.adminIdeals = (callback, filter, page = 0, maxPerPage = 20) => {
    this.doCall('admin/ideal', 'GET', callback, { filter, page, maxPerPage }, true);
  };

  this.checkIdeal = (transaction, callback) => {
    this.doCall(`ideal/${transaction}/validate2`, 'PUT', callback, null, true);
  };

  this.createProfile = (profile, callback) => {
    this.doCall('profile', 'POST', callback, profile, true);
  };

  this.modifyProfile = (profileId, profile, callback) => {
    this.doCall(`profile/${profileId}`, 'PUT', callback, profile, true);
  };

  this.deleteProfile = (profile, callback) => {
    this.doCall(`profile/${profile}`, 'DELETE', callback, null, true);
  };

  this.getAsset = (asset, callback) => {
    this.doCall(`asset/${asset}`, 'GET', callback, null, true);
  };

  this.updateAsset = (asset, friendlyName, description, sustainabilityScore, link, callback) => {
    this.doCall(`asset/${asset}`, 'PUT', callback, {
      friendly_name: friendlyName,
      description,
      sustainable_score: sustainabilityScore,
      link,
    }, true);
  };

  this.getAssetCosts = (asset, callback) => {
    this.doCall(`asset/${asset}/costs`, 'GET', callback, null, true);
  };

  this.getAssetCostsPort = (port, callback, start, end) => {
    this.doCall(`port/${port}/costs`, 'GET', callback, {
      start,
      end,
    }, true);
  };

  this.setPortClosed = (port, callback) => {
    this.doCall(`port/${port}/closed`, 'PUT', callback, null, true);
  };

  this.createAssetCost = (
    asset,
    date,
    transaction,
    onetime,
    incidental,
    expenses,
    spread,
    callback,
  ) => {
    this.doCall(`asset/${asset}/costs`, 'POST', callback, {
      date,
      transaction,
      onetime,
      incidental,
      expenses,
      spread,
    }, true);
  };

  this.removeAssetCost = (asset, date, callback) => {
    this.doCall(`asset/${asset}/costs/${date}`, 'DELETE', callback, null, true);
  };

  this.getUnreadMessages = (callback) => {
    this.doCall('message/new', 'GET', callback, null, true);
  };

  this.setMessageRead = (message, callback) => {
    this.doCall(`message/${message}/read`, 'PUT', callback, null, true);
  };

  this.setAllMessageRead = (callback) => {
    this.doCall('message/read', 'PUT', callback, null, true);
  };

  this.createMessage = (user, title, message, port, type, callback, overwriteBrand = null) => {
    this.doCall('message', 'POST', callback, {
      message,
      title,
      user,
      port,
      type,
      overwriteBrand,
    }, true);
  };

  this.generalContact = (message, callback, subject = null) => {
    this.doCall('contact', 'POST', callback, {
      message,
      subject,
    }, true);
  };

  this.portContact = (message, port, callback, subject = null) => {
    this.doCall(`port/${port}/contact`, 'POST', callback, {
      message,
      subject,
    }, true);
  };

  this.intakeContact = (message, intake, callback, subject = null) => {
    this.doCall(`intake/${intake}/contact`, 'POST', callback, {
      message,
      subject,
    }, true);
  };

  this.getAtpDetails = (callback) => {
    this.doCall('atp/details', 'GET', callback, null, true);
  };

  this.getAtpDetailsForUser = (advisor, callback) => {
    this.doCall(`user/${advisor}/atp`, 'GET', callback, null, true);
  };

  this.updateAtpDetails = (
    callback,
    city,
    name,
    street,
    housenr,
    housenrAddition,
    postal,
    iban,
    website,
    phone,
    email,
    advisorName,
    advisorInfix,
    advisorSurname,
    totalAdvisors,
    customers,
    kvk,
  ) => {
    this.doCall('atp/details', 'PUT', callback, {
      city,
      name,
      street,
      housenr,
      housenr_addition: housenrAddition,
      postal,
      iban,
      website,
      phone,
      email,
      advisor_name: advisorName,
      advisor_infix: advisorInfix,
      advisor_surname: advisorSurname,
      total_advisors: totalAdvisors,
      customers,
      kvk,
    }, true);
  };

  this.getAtpCustomersGroups = (callback) => {
    this.doCall('atp/customergroups', 'GET', callback, null, true);
  };

  this.getAtpCustomers = (
    callback,
    filter,
    page = 0,
    maxPerPage = 20,
    sort = null,
    sortDirection = null,
  ) => {
    this.doCall('atp/customers', 'GET', callback, {
      filter,
      page,
      maxPerPage,
      sort,
      sortDirection,
    }, true);
  };

  this.linkIntakeUserAtp = (intake, email, name, sex, callback) => {
    this.doCall(`atp/${intake}/user`, 'PUT', callback, {
      email,
      name,
      sex,
    }, true);
  };

  this.pushOffersIntake = (intake, offers, callback) => {
    this.doCall(`atp/${intake}`, 'put', callback, {
      offers,
    }, true);
  };

  this.quickAccept = (intake, callback, message = true) => {
    this.doCall(`intake/${intake}/quickaccept`, 'PUT', callback, { message }, true);
  };

  this.newUPerson = (intake, fromUser, toUser, callback) => {
    this.doCall(`intake/${intake}/person/${fromUser}`, 'PUT', callback, { to: toUser }, true);
  };

  this.deletePerson = (intake, user, callback) => {
    this.doCall(`intake/${intake}/person/${user}`, 'DELETE', callback, null, true);
  };

  this.offerCalc = (
    type,
    amount,
    profile,
    duration,
    urendement,
    forehand,
    target,
    callback,
    overwrite = false,
  ) => {
    this.doCall('offer', 'POST', callback, {
      type,
      amount,
      profile,
      duration,
      urendement,
      forehand,
      target,
      overwrite,
    }, true);
  };

  this.ping = (callback) => {
    this.doCall('ping', 'GET', callback);
  };

  this.isLoggedin = readCookie('vmvb_expire');
  this.role = readCookie('vmvb_role');
  this.client = readCookie('vmvb_id');
  this.name = readCookie('vmvb_name');
  this.isBecome = readCookie('vmvb_become');
  this.nps = readCookie('vmvb_nps');
  this.refreshing = false;
  this.lastFile = null;

  this.cookiesEnabled = () => {
    createCookie('cookie_test', '1');
    const ret = readCookie('cookie_test') === '1';
    eraseCookie('cookie_test');
    return ret;
  };

  this.doCall = (
    url,
    type,
    callback,
    data = null,
    secure = false,
    file = false,
    postFile = false,
    pdf = false,
  ) => {
    if (
      url !== 'refresh'
      && new Date(Number(readCookie('vmvb_expire'))) < new Date()
      && readCookie('vmvb_token')
      && !this.refreshing
    ) {
      this.refreshing = true;
      this.refresh();
    }
    const request = {
      url: `${process.env.VUE_APP_API}/${url}`,
      method: type,
    };
    request.cache = false;
    request.headers = {
      'env-style': process.env.VUE_APP_STYLE,
      'env-origin': process.env.VUE_APP_ORIGIN,
    };
    if (postFile) {
      request.headers['Content-Type'] = 'multipart/form-data';
    }
    if (readCookie('_ga')) {
      request.headers.cid = readCookie('_ga').split('.').slice(-2).join('.');
    }
    if (secure) {
      if (!readCookie('vmvb_token')) {
        callback({
          status: 401,
          error: 'No token',
        });
        return;
      }
      request.headers.Authorization = `Bearer ${readCookie('vmvb_token')}`;
    }
    if (file) {
      request.responseType = 'arraybuffer';
    }
    if (data !== null) {
      if (data.cache) {
        request.cache = { ttl: 1000 * 60 * data.cache };
      }
      delete data.cache;
      if (type === 'GET') {
        request.params = data;
      } else {
        request.data = data;
      }
    }
    api(request)
      .catch((error) => {
        if (error.response) {
          return error.response;
        }
        return {
          status: 500,
          error,
        };
      })
      .then((response) => {
        if (response.status === 200 && response.data.token) {
          if (url.substring(0, 5) === 'login' && request.data.two_factor) {
            createCookie('vmvb_2fa', 1, 100000);
          }
          if (url.substring(0, 6) === 'become') {
            createCookie('vmvb_become', 1);
          } else if (url === 'unbecome') {
            eraseCookie('vmvb_become');
          }
          createCookie('vmvb_token', response.data.token);
          createCookie('vmvb_id', response.data.id);
          createCookie('vmvb_name', response.data.name);
          createCookie('vmvb_role', response.data.role);
          if (response.data.nps) {
            createCookie('vmvb_nps', response.data.nps);
          }
          createCookie('vmvb_expire', (new Date()).setMinutes((new Date().getMinutes() + 15)), 15);
        }
        if (file) {
          if (pdf) {
            return { blob: new Blob([response.data], { type: 'application/pdf' }), status: response.status };
          }
          return { blob: new Blob([response.data]), status: response.status };
        }
        return response;
      })
      .then(callback);
  };

  this.sdkVersion = 10048;
}

export default {
  install: (app) => {
    app.config.globalProperties.sdk = new VMVB();
  },
};
